import utils from "./utils.js";

// Thumbnail slider: drag, scroll, loop-wrap, fade-mask, buttons.

const thumbnailMethods = {
    _bindThumbScroll() {
        const track = this.thumbsTrack;
        if (!track) return;
        this._evThumbScroll = (e) => {
            if (this._thumbIsGrid()) return;
            const isVert = this._isThumbVert();
            // Use the axis that matches the strip orientation; fall back to the other.
            const delta = isVert
                ? e.deltaY || e.deltaX
                : e.deltaX || e.deltaY;
            if (delta === 0) return;
            e.preventDefault();
            // Normalise pixel delta — deltaMode 0 = px, 1 = line (~20px), 2 = page (~300px)
            const px =
                e.deltaMode === 2
                    ? delta * 300
                    : e.deltaMode === 1
                    ? delta * 20
                    : delta;
            this._ts.offset += this._isRTL && !isVert ? -px : px;
            this._clampThumbOffset();
        };
        track.addEventListener("wheel", this._evThumbScroll, { passive: false });
    },

    _bindThumbDrag() {
        const list = this.thumbsList;
        const ts = this._ts;
        // Evaluated on every drag event so stacked-state changes are respected.
        const getPos = (e) =>
            this._isThumbVert()
                ? e.touches
                    ? e.touches[0].clientY
                    : e.clientY
                : e.touches
                ? e.touches[0].clientX
                : e.clientX;

        const start = (e) => {
            ts.dragging = true;
            ts.startPos = getPos(e);
            ts.startOffset = ts.offset;
            list.style.transition = "none";
        };
        const move = (e) => {
            if (!ts.dragging) return;
            const delta = ts.startPos - getPos(e);
            ts.offset =
                ts.startOffset +
                (this._isRTL && !this._isThumbVert() ? -delta : delta);
            this._applyThumbOffset(false);
        };
        const end = () => {
            ts.dragging = false;
            list.style.transition = "";
            this._clampThumbOffset();
        };

        utils.on(list, "mousedown", start);
        utils.on(list, "touchstart", start, { passive: true });
        utils.on(document, "mousemove", move);
        utils.on(document, "touchmove", move, { passive: true });
        utils.on(document, "mouseup", end);
        utils.on(document, "touchend", end);
    },

    // Returns the effective per-item size (px) for the current axis + mobile state.
    // Used by all slider scroll/clamp/offset methods to keep JS calculations in sync
    // with the CSS variable that controls the actual rendered item dimension.
    _thumbItemSize() {
        const isVert = this._isThumbVert();
        const w = this._mobileLayoutActive && this.opts.mobileThumbnailWidth > 0
            ? this.opts.mobileThumbnailWidth : this.opts.thumbnailWidth;
        let h = this._mobileLayoutActive && this.opts.mobileThumbnailHeight > 0
            ? this.opts.mobileThumbnailHeight : this.opts.thumbnailHeight;
        // When aspect ratio is active it overrides height; compute effective height from width.
        const aspect = this.opts.thumbnailAspectRatio;
        if (aspect && aspect !== "auto") {
            const parts = aspect.split("/");
            if (parts.length === 2) {
                h = Math.round(w * (parseFloat(parts[1]) / parseFloat(parts[0])));
            }
        }
        return (isVert ? h : w) + this.opts.thumbnailGap;
    },

    // Returns true when the thumbnail strip is in grid mode.
    // Always false on mobile (slider is always forced on mobile).
    _thumbIsGrid() {
        if (this._mobileLayoutActive) return false;
        return this.opts.thumbnailLayout === "grid";
    },

    // Returns true when thumbs are currently laid out vertically.
    // On mobile, uses mobileThumbnailPosition so left/right strips scroll vertically.
    // On desktop, left/right positions become horizontal when wcpg-stacked is active.
    _isThumbVert() {
        if (this._mobileLayoutActive) {
            return ["left", "right"].includes(
                this.opts.mobileThumbnailPosition || "bottom",
            );
        }
        return (
            ["left", "right"].includes(this.opts.thumbnailPosition) &&
            !this.wrapper.classList.contains("wcpg-stacked")
        );
    },

    _updateThumbActive(index) {
        this.thumbEls &&
            this.thumbEls.forEach((el, i) => {
                el.classList.toggle("wcpg-thumb-active", i === index);
                el.setAttribute(
                    "aria-selected",
                    i === index ? "true" : "false",
                );
            });
        this._updateThumbButtons();
        this._updateMainArrows(index);
    },

    _slideThumb(dir) {
        const isVert = this._isThumbVert();
        const size = this._thumbItemSize();
        const track = isVert
            ? this.thumbsTrack?.offsetHeight || 0
            : this.thumbsTrack?.offsetWidth || 0;
        const listGutter = this.opts.thumbnailActiveBorderWidth + 1;
        const total = size * this.items.length - this.opts.thumbnailGap;
        const standardMax = Math.max(0, total - track + listGutter);

        if (this.opts.thumbnailSlideType === "item" && size > 0) {
            // ── Item mode: snap to item-aligned positions ─────────────────
            // Each click advances the strip by exactly one item. We use
            // index-based movement so the behaviour is consistent regardless
            // of how small standardMax is (e.g. when items nearly fill the
            // track and standardMax < size).
            const visibleItems = Math.max(1, Math.floor(track / size));
            const maxIndex = Math.max(0, this.items.length - visibleItems);
            const itemMax = maxIndex * size;

            const atItemStart = this._ts.offset <= 0;
            const atItemEnd = this._ts.offset >= itemMax;
            const atBoundary =
                (dir < 0 && atItemStart) || (dir > 0 && atItemEnd);

            if (atBoundary && this.opts.thumbnailLoop && itemMax > 0) {
                this._thumbLoopWrap(dir, itemMax);
                return;
            }

            const currentIndex = Math.round(this._ts.offset / size);
            const newIndex = utils.clamp(currentIndex + dir, 0, maxIndex);
            this._ts.offset = newIndex * size;
            this._applyThumbOffset(true);
            this._updateThumbButtons();
            this._updateFadeMask();
            return;
        }

        // ── Row mode: slide by a full visible page ────────────────────────
        const max = standardMax;
        // Check whether we are at the boundary in the direction of travel
        const atStart = this._ts.offset <= 0;
        const atEnd = this._ts.offset >= max;
        const atBoundary = (dir < 0 && atStart) || (dir > 0 && atEnd);

        if (atBoundary && this.opts.thumbnailLoop && max > 0) {
            // Wrap-around: infinite-loop clone technique
            this._thumbLoopWrap(dir, max);
            return;
        }

        const visibleCount =
            size > 0 ? track / size : this.opts.thumbnailVisible;
        const step = Math.max(1, Math.floor(visibleCount));
        this._ts.offset += dir * size * step;
        this._clampThumbOffset();
    },

    _thumbLoopWrap(dir, max) {
        if (
            !this.thumbsList ||
            this._ts.loopAnimating ||
            this._ts.scrollWrapAnimating
        )
            return;
        this._ts.loopAnimating = true;

        const isVert = this._isThumbVert();
        const list = this.thumbsList;
        const itemSize = this._thumbItemSize();
        const trackSize = isVert
            ? this.thumbsTrack?.offsetHeight || 0
            : this.thumbsTrack?.offsetWidth || 0;
        const visible =
            itemSize > 0
                ? trackSize / itemSize
                : this.opts.thumbnailVisible || 5;
        const step =
            this.opts.thumbnailSlideType === "item"
                ? 1
                : Math.max(1, Math.floor(visible));
        const cloneCount = Math.min(step, this.items.length);
        const slideSize = itemSize * cloneCount;
        const ANIM_DUR = 320; // matches _applyThumbOffset CSS transition

        const cleanup = (newOffset) => {
            list.querySelectorAll(".wcpg-thumb-clone").forEach((c) =>
                c.remove(),
            );
            this._ts.offset = newOffset;
            this._applyThumbOffset(false);
            this._ts.loopAnimating = false;
            this._updateThumbButtons();
            this._updateFadeMask();
        };

        const makeClone = (el) => {
            const cl = el.cloneNode(true);
            cl.classList.add("wcpg-thumb-clone");
            cl.setAttribute("aria-hidden", "true");
            cl.style.pointerEvents = "none";
            return cl;
        };

        if (dir > 0) {
            // ── Forward wrap (end → start) ───────────────────────────────
            // Append clones of the first items so the list appears to continue rightward.
            for (let i = 0; i < cloneCount; i++) {
                list.appendChild(
                    makeClone(this.thumbEls[i % this.items.length]),
                );
            }
            // Animate immediately — same timing as a regular slide.
            // The transform departs from the already-committed translateX(-max) value,
            // so no rAF is needed here (unlike the backward case).
            this._ts.offset += slideSize;
            this._applyThumbOffset(true);
            setTimeout(() => cleanup(0), ANIM_DUR + 20);
        } else {
            // ── Backward wrap (start → end) ──────────────────────────────
            // Prepend clones of the last items so the list appears to continue leftward.
            // Insert in reverse order so the DOM order stays [last-N … last, item0, …].
            for (let i = cloneCount - 1; i >= 0; i--) {
                list.insertBefore(
                    makeClone(
                        this.thumbEls[this.items.length - cloneCount + i],
                    ),
                    list.firstChild,
                );
            }
            // Instantly reposition so the visible content is unchanged after prepend.
            this._ts.offset = slideSize;
            this._applyThumbOffset(false);
            // Next frame: animate backward into the prepended clones.
            requestAnimationFrame(() => {
                this._ts.offset = 0;
                this._applyThumbOffset(true);
                setTimeout(() => cleanup(max), ANIM_DUR + 20);
            });
        }
    },

    _scrollThumbToIndex(index, dir = 0, isWrap = false) {
        if (this._thumbIsGrid()) return;
        const isVert = this._isThumbVert();
        const size = this._thumbItemSize();
        const track = isVert
            ? this.thumbsTrack?.offsetHeight || 0
            : this.thumbsTrack?.offsetWidth || 0;
        // Center the active item in the track so adjacent partials
        // are visible at both edges (matches the fade-mask design).
        let offset;
        if (!isVert && this._isRTL) {
            const total = size * this.items.length - this.opts.thumbnailGap;
            const rtlItemCenter = total - index * size - size / 2;
            offset = track / 2 - rtlItemCenter;
        } else {
            const itemCenter = index * size + size / 2;
            offset = itemCenter - track / 2;
        }

        // For loop wrap navigation, animate the strip in the same direction as the
        // main image so it feels like a continuous infinite loop instead of rewinding.
        const listGutter = this.opts.thumbnailActiveBorderWidth + 1;
        const total = size * this.items.length - this.opts.thumbnailGap;
        const max = Math.max(0, total - track + listGutter);

        if (
            isWrap &&
            dir !== 0 &&
            this.opts.thumbnailLoop &&
            max > 0 &&
            this.thumbsList &&
            !this._ts.loopAnimating &&
            !this._ts.scrollWrapAnimating
        ) {
            const clampedOff = utils.clamp(offset, 0, max);
            this._scrollThumbWrapTo(dir, clampedOff);
            this._updateThumbButtons();
            this._updateFadeMask();
            return;
        }

        this._ts.offset = offset;
        this._clampThumbOffset();
    },

    // Animate the thumbnail strip in `dir` direction across the wrap boundary,
    // using a full clone set so the transition looks like an infinite loop.
    _scrollThumbWrapTo(dir, clampedOff) {
        this._ts.scrollWrapAnimating = true;
        const isVert = this._isThumbVert();
        const list = this.thumbsList;
        const size = this._thumbItemSize();
        const N = this.items.length;
        const setSize = N * size; // pixel span of one full item set (including inter-item gaps)
        const ANIM = 320; // ms — matches _applyThumbOffset CSS transition

        const cleanup = () => {
            list.querySelectorAll(".wcpg-thumb-clone").forEach((c) =>
                c.remove(),
            );
            this._ts.offset = clampedOff;
            this._applyThumbOffset(false);
            this._ts.scrollWrapAnimating = false;
            this._updateThumbButtons();
            this._updateFadeMask();
        };

        const makeClone = (el) => {
            const cl = el.cloneNode(true);
            cl.classList.add("wcpg-thumb-clone");
            cl.setAttribute("aria-hidden", "true");
            cl.style.pointerEvents = "none";
            return cl;
        };

        if (dir > 0) {
            // ── Forward wrap ─────────────────────────────────────────────
            // Append a full copy of all items after the real list.
            // Clone K sits at position setSize + K*size, identical to realK
            // after cleanup — so removing clones produces no visible jump.
            for (let i = 0; i < N; i++) {
                list.appendChild(makeClone(this.thumbEls[i]));
            }
            this._ts.offset = setSize + clampedOff;
            this._applyThumbOffset(true);
            setTimeout(cleanup, ANIM + 20);
        } else {
            // ── Backward wrap ─────────────────────────────────────────────
            // Prepend a full copy of all items before the real list.
            // Clone K sits at position K*size — same as realK will after cleanup.
            for (let i = N - 1; i >= 0; i--) {
                list.insertBefore(
                    makeClone(this.thumbEls[i]),
                    list.firstChild,
                );
            }
            // Instantly shift the offset by setSize so the real items stay at
            // the same visual position (they moved right by setSize in the DOM).
            this._ts.offset += setSize;
            this._applyThumbOffset(false);
            // Next frame: animate backward into the prepended clone set.
            requestAnimationFrame(() => {
                this._ts.offset = clampedOff;
                this._applyThumbOffset(true);
                setTimeout(cleanup, ANIM + 20);
            });
        }
    },

    _clampThumbOffset() {
        if (!this.thumbsList || this._thumbIsGrid())
            return;
        const isVert = this._isThumbVert();
        const size = this._thumbItemSize();
        const track = isVert
            ? this.thumbsTrack?.offsetHeight || 0
            : this.thumbsTrack?.offsetWidth || 0;
        // listGutter gives items breathing room from the overflow boundary
        // so the active border ring is never pixel-clipped by the track edge.
        const listGutter = this.opts.thumbnailActiveBorderWidth + 1;
        const total = size * this.items.length - this.opts.thumbnailGap;
        let max = Math.max(0, total - track + listGutter);
        // In item mode the strip can scroll further than standardMax so that
        // each click lands on a clean item-aligned offset.
        if (this.opts.thumbnailSlideType === "item" && size > 0) {
            const visibleItems = Math.max(1, Math.floor(track / size));
            max = Math.max(
                max,
                Math.max(0, this.items.length - visibleItems) * size,
            );
        }
        this._ts.offset = utils.clamp(this._ts.offset, 0, max);
        this._applyThumbOffset(true);
        this._updateThumbButtons();
        this._updateFadeMask();
    },

    _applyThumbOffset(animate) {
        if (!this.thumbsList || this._thumbIsGrid())
            return;
        const isVert = this._isThumbVert();
        const v = -this._ts.offset;
        this.thumbsList.style.transition = animate
            ? "transform 0.32s cubic-bezier(0.25,0.46,0.45,0.94)"
            : "none";
        this.thumbsList.style.transform = isVert
            ? `translateY(${v}px)`
            : this._isRTL
            ? `translateX(${-v}px)`
            : `translateX(${v}px)`;
        // Keep fade mask in sync during drag (non-animated calls)
        if (!animate) this._updateFadeMask();
    },

    _updateThumbButtons() {
        const isVert = this._isThumbVert();
        const size = this._thumbItemSize();

        // For vertical sliders without thumbnailFitToImage the wrap auto-sizes to
        // content height, so thumbsTrack.offsetHeight ≈ total items height and max
        // would always be > 0 (just listGutter), incorrectly showing arrows.
        // Use stage height as the effective visual constraint in that case.
        // Exception: stacked-gallery thumb mode — the stage spans the entire page,
        // so use the track's own CSS-constrained height (set via sticky max-height).
        const _useTrackH =
            this.opts.thumbnailFitToImage ||
            this.opts.stackedNavigationType === "thumbs";
        const track = isVert
            ? _useTrackH
                ? this.thumbsTrack?.offsetHeight || 0
                : this.stage?.offsetHeight || this.thumbsTrack?.offsetHeight || 0
            : this.thumbsTrack?.offsetWidth || 0;

        const listGutter = this.opts.thumbnailActiveBorderWidth + 1;
        const total = size * this.items.length - this.opts.thumbnailGap;
        let max = Math.max(0, total - track + listGutter);
        if (this.opts.thumbnailSlideType === "item" && size > 0) {
            const visibleItems = Math.max(1, Math.floor(track / size));
            max = Math.max(
                max,
                Math.max(0, this.items.length - visibleItems) * size,
            );
        }

        // Alignment: activate when all items fit in the track (no scrolling needed).
        // Horizontal: requires !thumbnailFitToImage (fit-to-image fills the full width,
        //   leaving no space for positioning within the track).
        // Vertical: always check regardless of thumbnailFitToImage — items rarely fill
        //   the full stage height, so alignment within it is always meaningful.
        if (this.thumbsWrap && !this._thumbIsGrid()) {
            this.thumbsWrap.classList.toggle(
                "wcpg-align-active",
                max <= 0 && (isVert || !this.opts.thumbnailFitToImage),
            );
        }

        // Nav arrows: hide completely when there is nothing to scroll in that direction.
        if (
            !this.thumbPrev ||
            !this.thumbNext ||
            this._thumbIsGrid()
        )
            return;
        const loop = this.opts.thumbnailLoop;
        const noScroll = max <= 0;
        this.thumbPrev.classList.toggle(
            "wcpg-btn-hidden",
            noScroll || (!loop && this._ts.offset <= 0),
        );
        this.thumbNext.classList.toggle(
            "wcpg-btn-hidden",
            noScroll || (!loop && this._ts.offset >= max),
        );
    },

    _updateFadeMask() {
        if (!this.thumbsTrack || this._thumbIsGrid())
            return;
        const isVert = this._isThumbVert();
        const size = this._thumbItemSize();
        const track = isVert
            ? this.thumbsTrack.offsetHeight
            : this.thumbsTrack.offsetWidth;
        const listGutter = this.opts.thumbnailActiveBorderWidth + 1;
        const total = size * this.items.length - this.opts.thumbnailGap;
        let max = Math.max(0, total - track + listGutter);
        if (this.opts.thumbnailSlideType === "item" && size > 0) {
            const visibleItems = Math.max(1, Math.floor(track / size));
            max = Math.max(
                max,
                Math.max(0, this.items.length - visibleItems) * size,
            );
        }
        const FADE = "36px";
        const dir = isVert ? "to bottom" : "to right";
        const rtlHoriz = !isVert && this._isRTL;
        const hasLeft = rtlHoriz
            ? this._ts.offset < max - 2
            : this._ts.offset > 2;
        const hasRight = rtlHoriz
            ? this._ts.offset > 2
            : this._ts.offset < max - 2;

        let mask;
        if (hasLeft && hasRight) {
            mask = `linear-gradient(${dir}, transparent 0px, black ${FADE}, black calc(100% - ${FADE}), transparent 100%)`;
        } else if (hasLeft) {
            mask = `linear-gradient(${dir}, transparent 0px, black ${FADE}, black 100%)`;
        } else if (hasRight) {
            mask = `linear-gradient(${dir}, black 0%, black calc(100% - ${FADE}), transparent 100%)`;
        } else {
            mask = "none";
        }

        this.thumbsTrack.style.webkitMaskImage = mask;
        this.thumbsTrack.style.maskImage = mask;
    },
};

export default thumbnailMethods;
